import React, { useCallback, useEffect, useMemo, useState } from "react";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Typography } from "@mui/material";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection";
import { useNotification } from "@context/NotificationContext";
import { useCheckOwnersMatter } from "@features/vessel-call/vessel-call-status/hooks/useCheckOwnersMatter.ts";
import PreviousTransfers from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/components/PreviousTransfers/PreviousTransfers";
import RejectShippingAgencyTransferDrawer from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/components/RejectShippingAgencyTransferDrawer/RejectShippingAgencyTransferDrawer";
import ShippingAgencyTransferDetails from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/components/ShippingAgencyTransferDetails/ShippingAgencyTransferDetails";
import ShippingAgencyTransferRequest from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/components/ShippingAgencyTransferRequest/ShippingAgencyTransferRequest";
import ShippingAgencyTransferFlowButtons from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/flow/ShippingAgencyTransferFlowButtons";
import { SHIPPING_AGENCY_TRANSFER_STATUS } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/flow/utils.tsx";
import { useCompleteShippingAgencyTransfer } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/hooks/useCompleteShippingAgencyTransfer";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { getDepartureCallById } from "@services/departureServiceApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import {
  cancelShippingAgencyTransfer,
  getLatestShippingAgencyTransfer,
  getShippingAgencyTransfers,
} from "@services/shippingAgencyTransferApi";
import type { Company } from "@services/vessel-call/types";
import { getVesselCallById } from "@services/vesselCallApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { runTransaction } from "@telemetry/runTransaction";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import DetailsSideDrawer from "./components/DetailsSideDrawer/DetailsSideDrawer";

export const LANG_BASE = "vessel-call.shipping-agency-transfer";
const SERVICE = "bff-vessel-call";
const TEST_ID_BASE = "shipping-agency-transfer";
const PREVIOUS_TRANSFERS_INQUIRY_ID = "shipping-agency-transfer.previous-transfers.search";
const PREVIOUS_TRANSFERS_INQUIRY_CONTEXT = {
  module: "vessel_call",
  screen: "Shipping Agency Transfer",
  action: "search_previous_shipping_agency_transfers",
  entityType: "shipping_agency_transfer",
};

const ShippingAgencyTransfer: React.FC = () => {
  const navigate = useNavigate();
  const { id: vesselCallId } = useParams();
  const { t } = useTranslation();

  const safeVesselCallId = vesselCallId ?? "";
  const [openDrawers, setOpenDrawers] = useState<{ [key: string]: boolean }>({
    rejectDrawer: false,
    statusDrawer: false,
  });

  const [isConfirmationWindowOpen, setIsConfirmationWindowOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [shouldRedirectToGrid, setShouldRedirectToGrid] = useState(false);

  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const { user, userAccessPermissions, isMarineDepartmentUser } = useAuthStore();
  const isTerminalUser = user?.roles?.includes("terminal") ?? false;

  const currentShippingAgency = user?.companyId ?? "";

  const hasShippingAgencyTransferAccess = useMemo(
    () => checkPermission(userAccessPermissions, "hasShippingAgencyTransferAccess"),
    [userAccessPermissions],
  );

  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);

  const handleOpenDrawer = (drawer: string) =>
    setOpenDrawers((prev) => ({ ...prev, [drawer]: true }));

  const handleCloseDrawer = (drawer: string) =>
    setOpenDrawers((prev) => ({ ...prev, [drawer]: false }));

  const transfersQueryKey = useMemo(
    () => ["getShippingAgencyTransfers", safeVesselCallId, pageNumber, pageSize] as const,
    [safeVesselCallId, pageNumber, pageSize],
  );

  const baseParams = {
    pageSize,
    pageNumber,
    vesselCallId: safeVesselCallId,
  };

  const {
    data: shippingAgencyApprovedTransfersData,
    isFetching: isApprovedTransfersFetching,
    isLoading: isApprovedTransfersLoading,
    isError: isApprovedTransfersError,
    isSuccess: isApprovedTransfersSuccess,
    dataUpdatedAt: approvedTransfersDataUpdatedAt,
    error: approvedTransfersError,
  } = useQuery({
    queryKey: [...transfersQueryKey, "approved"],
    queryFn: () =>
      getShippingAgencyTransfers(SERVICE, {
        ...baseParams,
        statuses: [SHIPPING_AGENCY_TRANSFER_STATUS.APPROVED],
      }),
    enabled: !isTerminalUser && !!safeVesselCallId && isMarineDepartmentUser,
  });
  useTrackPendingInquiry(PREVIOUS_TRANSFERS_INQUIRY_ID, {
    isFetching: isApprovedTransfersFetching,
    isLoading: isApprovedTransfersLoading,
    isError: isApprovedTransfersError,
    isSuccess: isApprovedTransfersSuccess,
    error: approvedTransfersError,
    dataUpdatedAt: approvedTransfersDataUpdatedAt,
  });

  const {
    data: lastTransfer,
    isFetching: isAllTransfersFetching,
    isSuccess: isLatestTransferSuccess,
  } = useQuery({
    queryKey: ["getLatestShippingAgencyTransfer", safeVesselCallId],
    queryFn: () => getLatestShippingAgencyTransfer(SERVICE, { vesselCallId: safeVesselCallId }),
    enabled: !isTerminalUser && !!safeVesselCallId,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const approvedTransfers = shippingAgencyApprovedTransfersData?.shippingAgencyTransfers ?? [];
  const latestTransfer =
    lastTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_APPROVAL ||
    lastTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_MARINE_DEPARTMENT_APPROVAL ||
    lastTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.APPROVED ||
    lastTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.REJECTED
      ? lastTransfer
      : undefined;

  const hasPendingApprovalTransfer =
    latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_APPROVAL ||
    latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_MARINE_DEPARTMENT_APPROVAL;

  const { data: vesselCallDetails } = useQuery({
    queryKey: ["vesselCall", SERVICE, safeVesselCallId],
    queryFn: () => getVesselCallById(SERVICE, safeVesselCallId),
    enabled: !isTerminalUser && !!safeVesselCallId,
    staleTime: 0,
    refetchOnMount: true,
  });

  const { data: departureDetails } = useQuery({
    queryKey: ["vessel-departure-details", safeVesselCallId],
    queryFn: () => getDepartureCallById(safeVesselCallId),
    enabled: !isTerminalUser && !!safeVesselCallId,
  });

  const vesselId = vesselCallDetails?.vesselId ?? "";

  const isUserMainShippingAgent = useMemo(
    () => vesselCallDetails?.companyId === currentShippingAgency,
    [vesselCallDetails?.companyId, currentShippingAgency],
  );

  const isNewShippingAgency = useMemo(
    () => latestTransfer?.newShippingAgency?.companyCode === currentShippingAgency,
    [latestTransfer?.newShippingAgency?.companyCode, currentShippingAgency],
  );

  const isCurrentOwnerMattersAgent = useMemo(
    () => vesselCallDetails?.ownersMattersAgent === currentShippingAgency,
    [vesselCallDetails, currentShippingAgency],
  );

  const isMarineDepartment = useMemo(
    () => checkPermission(userAccessPermissions, "hasTaskAccess"),
    [userAccessPermissions],
  );

  const transfersForMarineDepartment =
    latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_MARINE_DEPARTMENT_APPROVAL ||
    latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.APPROVED ||
    latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.REJECTED
      ? latestTransfer
      : undefined;

  const badgeType = useMemo(() => {
    const status = isMarineDepartment
      ? transfersForMarineDepartment?.status
      : latestTransfer?.status;
    switch (status) {
      case SHIPPING_AGENCY_TRANSFER_STATUS.APPROVED:
        return "success";
      case SHIPPING_AGENCY_TRANSFER_STATUS.REJECTED:
        return "error";
      case SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_APPROVAL:
      case SHIPPING_AGENCY_TRANSFER_STATUS.COMPLETED:
      default:
        return "warning";
    }
  }, [isMarineDepartment, transfersForMarineDepartment?.status, latestTransfer?.status]);

  const canViewTransferDetails = useMemo(
    () =>
      !!latestTransfer &&
      (hasShippingAgencyTransferAccess ||
        isUserMainShippingAgent ||
        isNewShippingAgency ||
        isMarineDepartment ||
        isCurrentOwnerMattersAgent),
    [
      latestTransfer,
      hasShippingAgencyTransferAccess,
      isUserMainShippingAgent,
      isNewShippingAgency,
      isMarineDepartment,
      isCurrentOwnerMattersAgent,
    ],
  );

  const canRequestTransfer = useMemo(
    () => isUserMainShippingAgent && isLatestTransferSuccess && !hasPendingApprovalTransfer,
    [isUserMainShippingAgent, isLatestTransferSuccess, hasPendingApprovalTransfer],
  );

  const handleError = useCallback(
    (error: Error) =>
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      }),
    [notify],
  );

  const handleSuccess = useCallback(async () => {
    setIsConfirmationWindowOpen(false);
    await queryClient.invalidateQueries({
      queryKey: ["getShippingAgencyTransfers", safeVesselCallId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["getLatestShippingAgencyTransfer", safeVesselCallId],
    });
  }, [queryClient, safeVesselCallId]);

  const cancelTransferMutation = useMutation({
    mutationFn: () => {
      const transferId = latestTransfer?.id;
      if (!transferId) {
        return Promise.reject(new Error(t("common.something-went-wrong")));
      }
      return runTransaction(
        {
          module: "vessel_call",
          screen: "ShippingAgencyTransfer",
          action: "cancel_shipping_agency_transfer",
          entityType: "shipping_agency_transfer",
        },
        (transactionId) =>
          cancelShippingAgencyTransfer(
            SERVICE,
            transferId,
            ...(transactionId ? [transactionId] : []),
          ),
      );
    },
    onSuccess: () => handleSuccess(),
    onError: (error: Error) => handleError(error),
  });

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    SERVICE,
  );

  const ownersMattersName = useMemo(() => {
    return companies.find((c) => c.companyCode === transfersForMarineDepartment?.ownersMatter)
      ?.companyNameEnglish;
  }, [companies, transfersForMarineDepartment]);

  const openCancelDialog = useCallback(() => setIsConfirmationWindowOpen(true), []);
  const closeCancelDialog = useCallback(() => setIsConfirmationWindowOpen(false), []);
  const confirmCancel = useCallback(
    () => cancelTransferMutation.mutate(),
    [cancelTransferMutation],
  );

  const handleMutationSuccess = async (message: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["getShippingAgencyTransfers", safeVesselCallId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["getLatestShippingAgencyTransfer", safeVesselCallId],
    });

    notify({
      title: message,
      type: "success",
    });
  };

  const handleRejectSuccess = async (message: string) => {
    if (!isMarineDepartment) {
      await queryClient.invalidateQueries({
        queryKey: ["getShippingAgencyTransfers", safeVesselCallId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["getLatestShippingAgencyTransfer", safeVesselCallId],
      });
      notify({
        title: message,
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["vesselCall"] });
      setShouldRedirectToGrid(true);
      return;
    }

    await handleMutationSuccess(message);
  };

  useEffect(() => {
    if (!shouldRedirectToGrid) return;
    navigate(`/${paths.vesselCall.index}`, { replace: true });
  }, [navigate, shouldRedirectToGrid]);

  const handleApproveSuccess = async () => {
    await handleMutationSuccess(t(`${LANG_BASE}.notifications.transfer-approved`));
    setIsApproveConfirmOpen(false);
  };

  const handleApproveError = (mutationError: any) => {
    setIsApproveConfirmOpen(false);

    notify({
      title: t("common.error"),
      description:
        mutationError?.response?.data?.detail ??
        mutationError?.message ??
        t("common.something-went-wrong"),
      type: "error",
    });
  };
  const { completeShippingAgencyTransfer } = useCompleteShippingAgencyTransfer({
    handleSuccess: handleApproveSuccess,
    handleError: handleApproveError,
  });

  const handleSubmitApprove = async () => {
    await completeShippingAgencyTransfer({
      id: latestTransfer?.id ?? "",
      service: SERVICE,
      payload: { status: "APPROVED" },
    });
  };

  const handlePreviousTransfersPageChange = useCallback(
    (nextPage: number) => {
      if (nextPage !== pageNumber) {
        startInquiryTiming(PREVIOUS_TRANSFERS_INQUIRY_ID, PREVIOUS_TRANSFERS_INQUIRY_CONTEXT);
      }
      setPageNumber(nextPage);
    },
    [pageNumber],
  );

  const handlePreviousTransfersPageSizeChange = useCallback(
    (nextPageSize: number) => {
      if (nextPageSize !== pageSize) {
        startInquiryTiming(PREVIOUS_TRANSFERS_INQUIRY_ID, PREVIOUS_TRANSFERS_INQUIRY_CONTEXT);
      }
      setPageSize(nextPageSize);
    },
    [pageSize],
  );

  const viewDetailsLink = (
    <>
      {"- "}
      <Link
        to="#"
        onClick={(e) => {
          e.preventDefault();
          setOpenDrawers((prev) => ({
            ...prev,
            statusDrawer: true,
          }));
        }}
        className="font-medium text-blue-800 hover:underline cursor-pointer"
      >
        {t(`${LANG_BASE}.view-details-link`)}
      </Link>
    </>
  );

  const ownersMatter = useCheckOwnersMatter(safeVesselCallId);

  const isProposedAgency =
    isLatestTransferSuccess &&
    (hasPendingApprovalTransfer ||
      latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.APPROVED) &&
    latestTransfer?.newShippingAgency?.companyCode === currentShippingAgency;

  const unauthorizedAcess =
    ownersMatter && isLatestTransferSuccess && !isAllTransfersFetching && !isProposedAgency;

  if (isTerminalUser) {
    return null;
  }

  if (unauthorizedAcess) {
    return <UnauthorizedRedirection />;
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <div>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {t(`${LANG_BASE}.title`)}
          </Typography>

          {isMarineDepartment && transfersForMarineDepartment && (
            <div
              className="mt-1 flex items-center gap-2"
              data-testid={`${TEST_ID_BASE}-status-badge`}
            >
              <Badge
                type={badgeType}
                label={t(`${LANG_BASE}.status-labels.${transfersForMarineDepartment.status}`)}
              />
              {transfersForMarineDepartment.status === "REJECTED" && viewDetailsLink}
            </div>
          )}

          {!isMarineDepartment && latestTransfer && (
            <div
              className="mt-1 flex items-center gap-2"
              data-testid={`${TEST_ID_BASE}-status-badge`}
            >
              <Badge
                type={badgeType}
                label={t(`${LANG_BASE}.status-labels.${latestTransfer.status}`)}
              />
              {latestTransfer.status === "REJECTED" && viewDetailsLink}
            </div>
          )}
        </div>

        <ShippingAgencyTransferFlowButtons
          transferDetails={latestTransfer ?? null}
          openDrawer={handleOpenDrawer}
          onApprove={() => setIsApproveConfirmOpen(true)}
        />

        {isUserMainShippingAgent && hasPendingApprovalTransfer && (
          <Button
            type="button"
            variant="outlined"
            color="primary"
            testId={`${TEST_ID_BASE}-cancel-button`}
            label={t(`${LANG_BASE}.cancel-button`)}
            sx={{ height: "40px", minHeight: "40px", textTransform: "none" }}
            onClick={openCancelDialog}
            startIcon={<HighlightOffIcon />}
            disabled={cancelTransferMutation.isPending || isAllTransfersFetching}
          />
        )}
      </div>

      {isMarineDepartment && canViewTransferDetails && transfersForMarineDepartment && (
        <ShippingAgencyTransferDetails
          ownersMatters={ownersMattersName ?? ""}
          newShippingAgency={
            transfersForMarineDepartment?.newShippingAgency.companyNameEnglish ?? ""
          }
          transferDate={transfersForMarineDepartment?.transferDateAndTime ?? ""}
          additionalDocuments={transfersForMarineDepartment?.additionalDocuments}
        />
      )}

      {!isMarineDepartment && canViewTransferDetails && (
        <ShippingAgencyTransferDetails
          newShippingAgency={latestTransfer?.newShippingAgency?.companyNameEnglish ?? ""}
          transferDate={latestTransfer?.transferDateAndTime ?? ""}
          additionalDocuments={latestTransfer?.additionalDocuments}
        />
      )}

      {canRequestTransfer && (
        <ShippingAgencyTransferRequest
          companies={companies}
          vesselId={vesselId}
          currentShippingAgency={currentShippingAgency}
          vesselCallId={safeVesselCallId}
          vesselCallStatus={vesselCallDetails?.status}
          departureStatus={departureDetails?.status}
        />
      )}

      {isMarineDepartmentUser && (
        <PreviousTransfers
          previousTransfers={approvedTransfers}
          totalCount={shippingAgencyApprovedTransfersData?.totalElements}
          pageNumber={pageNumber}
          onPageChange={handlePreviousTransfersPageChange}
          onPageSizeChange={handlePreviousTransfersPageSizeChange}
          pageSize={pageSize}
          companies={companies}
        />
      )}
      <GenericDialog
        open={isConfirmationWindowOpen}
        title={t(`${LANG_BASE}.confirmation-title`)}
        content={t(`${LANG_BASE}.confirmation-message`)}
        confirmButtonText={t("common.confirm")}
        cancelButtonText={t("common.cancel")}
        handleCloseDialog={closeCancelDialog}
        confirmAction={confirmCancel}
      />

      <RejectShippingAgencyTransferDrawer
        isOpen={openDrawers.rejectDrawer}
        transferId={latestTransfer?.id ?? ""}
        onClose={handleCloseDrawer}
        onSuccess={handleRejectSuccess}
      />
      <GenericDialog
        open={isApproveConfirmOpen}
        title={t(`${LANG_BASE}.confirm-transfer-title`)}
        content={t(`${LANG_BASE}.confirm-transfer-subtitle`)}
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t(`${LANG_BASE}.flow-buttons.approve-button`)}
        handleCloseDialog={() => setIsApproveConfirmOpen(false)}
        confirmAction={handleSubmitApprove}
        testId={`${TEST_ID_BASE}-approve-confirmation-dialog`}
        maxWidth="sm"
      />
      <DetailsSideDrawer
        isOpen={openDrawers.statusDrawer}
        testId={`${TEST_ID_BASE}-details-side-drawer`}
        onClose={() => setOpenDrawers((prev) => ({ ...prev, statusDrawer: false }))}
        rejectionReason={latestTransfer?.lastTaskDecision?.rejectionReason ?? ""}
        externalDocument={latestTransfer?.lastTaskDecision?.rejectionAttachmentsUrl}
        externalDocumentName={latestTransfer?.lastTaskDecision?.rejectionAttachmentsName}
        submittedBy={latestTransfer?.lastTaskDecision?.submittedBy ?? latestTransfer?.submittedBy}
        submittedByRole={latestTransfer?.lastTaskDecision?.submittedByRole}
        submittedAt={latestTransfer?.lastTaskDecision?.timestamp}
      />
    </div>
  );
};

export default ShippingAgencyTransfer;
